// The catalog's supporting lists (platform target model §7.5; parity backoffice pages/catalog.md §8, settings dining
// options, register W-26, W-30, W-60b): departments, courses, dining options, age restrictions and tax exemptions. Each is
// location config written at the node or in the cloud, one `ConfigFieldsWritten` fact per write, like products.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import {
  AgeRestrictionId, CourseId, DepartmentId, DiningOptionId, EmployeeId, LocationId, ProductId, TaxExemptionId,
} from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';

const Name = z.string().trim().min(1).max(120);
const SortKey = z.string().min(1).max(64);

/**
 * Legacy `departments` (reporting only; "Sales Category" in some copy). `sortKey` (B1993, optional) is legacy's order:
 * the importer writes it from the legacy id, since legacy lists departments in id order and imported ids made in the
 * same second don't sort that way. A department made in Next has none and comes after the keyed ones, by id (metrics
 * `inDepartmentOrder`).
 */
export const DepartmentFields = z.object({ name: Name, parentId: DepartmentId.nullable(), sortKey: SortKey.optional() }).meta({ id: 'DepartmentFields' });
export const DepartmentView = DepartmentFields.extend({ departmentId: DepartmentId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'DepartmentView' });

/** Legacy Pacing `courses` (name, ordering). */
export const CourseFields = z.object({ name: Name, sortKey: SortKey }).meta({ id: 'CourseFields' });
export const CourseView = CourseFields.extend({ courseId: CourseId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'CourseView' });

/** Legacy `dining_options` (name, `default`); at most one default per location (legacy enforced it in the app). */
export const DiningOptionFields = z
  .object({ name: Name, isDefault: z.boolean(), sortKey: SortKey })
  .meta({ id: 'DiningOptionFields' });
export const DiningOptionView = DiningOptionFields.extend({ diningOptionId: DiningOptionId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'DiningOptionView' });

/**
 * Legacy `id_verifications` (name, message, age_requirement, ordering): the register asks for ID before an order with an
 * unsent item carrying one is sent or paid (W-60b).
 */
export const AgeRestrictionFields = z
  .object({ name: Name, message: z.string().max(255), minimumAge: z.int().min(1).max(120), sortKey: SortKey })
  .meta({ id: 'AgeRestrictionFields' });
export const AgeRestrictionView = AgeRestrictionFields.extend({
  ageRestrictionId: AgeRestrictionId, locationId: LocationId.nullable(), version: z.int().nonnegative(),
}).meta({ id: 'AgeRestrictionView' });

/**
 * Legacy `tax_exempt_overrides` + `dining_option_tax_exempt_overrides`: a named rule that makes its products tax exempt
 * when the order's dining option is one of its dining options (PRC-019). Products name the rules they belong to.
 */
export const TaxExemptionFields = z
  .object({ name: Name, diningOptionIds: z.array(DiningOptionId).max(100) })
  .meta({ id: 'TaxExemptionFields' });
export const TaxExemptionView = TaxExemptionFields.extend({ taxExemptionId: TaxExemptionId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'TaxExemptionView' });

const configErrors = [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound] as const;
const common = { availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: configErrors, dryRun: true } as const;
const deleted = <T extends z.ZodType>(id: T) => z.object({ id, deleted: z.literal(true) });

export const UpsertDepartment = defineCommand({
  ...common,
  name: 'catalog.upsertDepartment',
  summary: 'Create a department or rename it.',
  // `shared: false` makes a new department the location's own (Launchpad's always does); left out, a new one is the account's.
  input: z.object({ departmentId: DepartmentId, locationId: LocationId, shared: z.boolean().optional(), changes: DepartmentFields.partial() }),
  output: DepartmentView,
  permission: 'items.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/departments', 'PUT /v1/departments/{id}'],
});

export const DeleteDepartment = defineCommand({
  ...common,
  name: 'catalog.deleteDepartment',
  summary: 'Delete a department; its items keep selling with no department.',
  input: z.object({ departmentId: DepartmentId, locationId: LocationId }),
  output: deleted(DepartmentId),
  permission: 'items.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['DELETE /v1/departments/{id}'],
});

export const UpsertCourse = defineCommand({
  ...common,
  name: 'catalog.upsertCourse',
  summary: 'Create or change a course (order pacing).',
  input: z.object({ courseId: CourseId, locationId: LocationId, changes: CourseFields.partial() }),
  output: CourseView,
  permission: 'settings.order_checkout.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/courses', 'PUT /v1/courses/{id}'],
});

export const DeleteCourse = defineCommand({
  ...common,
  name: 'catalog.deleteCourse',
  summary: 'Delete a course.',
  input: z.object({ courseId: CourseId, locationId: LocationId }),
  output: deleted(CourseId),
  permission: 'settings.order_checkout.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['DELETE /v1/courses/{id}'],
});

export const UpsertDiningOption = defineCommand({
  ...common,
  name: 'catalog.upsertDiningOption',
  summary: 'Create or change a dining option. Making one the default takes the flag from the previous default.',
  input: z.object({ diningOptionId: DiningOptionId, locationId: LocationId, changes: DiningOptionFields.partial() }),
  output: DiningOptionView,
  permission: 'settings.order_checkout.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/dining-options', 'PUT /v1/dining-options/{id}'],
});

export const DeleteDiningOption = defineCommand({
  ...common,
  name: 'catalog.deleteDiningOption',
  summary: 'Delete a dining option.',
  input: z.object({ diningOptionId: DiningOptionId, locationId: LocationId }),
  output: deleted(DiningOptionId),
  permission: 'settings.order_checkout.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/dining-options/{id}'],
});

export const UpsertAgeRestriction = defineCommand({
  ...common,
  name: 'catalog.upsertAgeRestriction',
  summary: 'Create or change an ID verification rule (minimum age and the message the register shows).',
  input: z.object({ ageRestrictionId: AgeRestrictionId, locationId: LocationId, changes: AgeRestrictionFields.partial() }),
  output: AgeRestrictionView,
  permission: 'items.manage',
  // B496: legacy's API checked no permission; the page needs Order & Checkout Settings and Manage Items (L686).
  backofficeAlso: ['settings.order_checkout.manage'],
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/id-verifications', 'PUT /v1/id-verifications/{id}'],
});

export const DeleteAgeRestriction = defineCommand({
  ...common,
  name: 'catalog.deleteAgeRestriction',
  summary: 'Delete an ID verification rule.',
  input: z.object({ ageRestrictionId: AgeRestrictionId, locationId: LocationId }),
  output: deleted(AgeRestrictionId),
  permission: 'items.manage',
  // B496: legacy's API checked no permission; the page needs Order & Checkout Settings and Manage Items (L686).
  backofficeAlso: ['settings.order_checkout.manage'],
  risk: 'low',
  agent: { expose: false },
  legacy: ['DELETE /v1/id-verifications/{id}'],
});

export const UpsertTaxExemption = defineCommand({
  ...common,
  name: 'catalog.upsertTaxExemption',
  summary: 'Create or change a tax exempt override: the dining options under which its products sell tax exempt.',
  input: z.object({ taxExemptionId: TaxExemptionId, locationId: LocationId, changes: TaxExemptionFields.partial() }),
  output: TaxExemptionView,
  permission: 'settings.location.manage',
  risk: 'high',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/tax-exempt-overrides', 'PUT /v1/tax-exempt-overrides/{id}'],
});

export const DeleteTaxExemption = defineCommand({
  ...common,
  name: 'catalog.deleteTaxExemption',
  summary: 'Delete a tax exempt override.',
  input: z.object({ taxExemptionId: TaxExemptionId, locationId: LocationId }),
  output: deleted(TaxExemptionId),
  permission: 'settings.location.manage',
  risk: 'high',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['DELETE /v1/tax-exempt-overrides/{id}'],
});

/** The 86 switch (W-74, RT-017): a product sells or shows "out of stock" on every register at the location. */
export const SetItemAvailability = defineCommand({
  ...common,
  name: 'catalog.setItemAvailability',
  summary: "Mark a product available or out of stock (86) at a location; every register shows the change at once.",
  // B692: `approvedBy`, the manager whose PIN unlocked Item Management for someone without item_management; every
  // receiver of a register's fact checks their grant (core storeWriteRefusal). It is not proof of the PIN.
  input: z.object({ productId: ProductId, locationId: LocationId, available: z.boolean(), approvedBy: EmployeeId.optional() }),
  output: z.object({ productId: ProductId, available: z.boolean() }),
  permission: 'items.availability',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['PUT /v1/products/{id}'],
});

const byLocation = z.object({ locationId: LocationId });
const listQuery = {
  availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] }, permission: { session: 'user' },
} as const;

export const ListDepartments = defineQuery({
  ...listQuery, name: 'catalog.departments.list', summary: "The location's departments.", input: byLocation,
  output: z.object({ departments: z.array(DepartmentView) }), legacy: ['GET /v1/departments'],
});
export const ListCourses = defineQuery({
  ...listQuery, name: 'catalog.courses.list', summary: "The location's courses, in pacing order.", input: byLocation,
  output: z.object({ courses: z.array(CourseView) }), legacy: ['GET /v1/courses'],
});
export const ListDiningOptions = defineQuery({
  ...listQuery, name: 'catalog.diningOptions.list', summary: "The location's dining options.", input: byLocation,
  output: z.object({ diningOptions: z.array(DiningOptionView) }), legacy: ['GET /v1/dining-options'],
});
export const ListAgeRestrictions = defineQuery({
  ...listQuery, name: 'catalog.ageRestrictions.list', summary: "The location's ID verification rules.", input: byLocation,
  output: z.object({ ageRestrictions: z.array(AgeRestrictionView) }), legacy: ['GET /v1/id-verifications'],
});
export const ListTaxExemptions = defineQuery({
  ...listQuery, name: 'catalog.taxExemptions.list', summary: "The location's tax exempt overrides.", input: byLocation,
  output: z.object({ taxExemptions: z.array(TaxExemptionView) }), legacy: ['GET /v1/dining-options/advanced-settings'],
});

export const menuCommands = [
  UpsertDepartment, DeleteDepartment, UpsertCourse, DeleteCourse, UpsertDiningOption, DeleteDiningOption, UpsertAgeRestriction,
  DeleteAgeRestriction, UpsertTaxExemption, DeleteTaxExemption, SetItemAvailability,
] as const;
export const menuQueries = [ListDepartments, ListCourses, ListDiningOptions, ListAgeRestrictions, ListTaxExemptions] as const;
